import { Editor } from '@tiptap/react';
import { TextSelection } from '@tiptap/pm/state';
import { type TableOfContentData } from '@tiptap/extension-table-of-contents';
import { type CSSProperties, memo, useMemo, useState } from 'react';
import clsx from 'clsx';

import { buildHierarchy, TreeNode } from '@/utils/helper-utils';

import styles from './table-of-contents.module.scss';

export const TableOfContents = memo((props: TableOfContentsProps) => {
  const { editor, content } = props;

  const [clickedActive, setClickedActive] = useState<string>();

  const data = useMemo(() => (content?.length ? buildHierarchy(content) : []), [content]);

  const onItemClick = (id: string) => {
    if (editor) {
      const element = editor.view.dom.querySelector(`[data-toc-id="${id}"]`);
      if (element) {
        const pos = editor.view.posAtDOM(element, 0);

        const tr = editor.view.state.tr;
        tr.setSelection(new TextSelection(tr.doc.resolve(pos)));

        editor.view.dispatch(tr);
        editor.view.focus();

        element.scrollIntoView({
          block: 'start',
        });
        setClickedActive(id);
      }
    }
  };

  const renderList = (nodes: TreeNode[]) => {
    return nodes.map((item) => (
      <li key={item.id} style={{ '--originalLevel': item.originalLevel } as CSSProperties}>
        <span
          title={item.textContent}
          onClick={() => onItemClick(item.id)}
          className={clsx({
            [styles.active]: clickedActive !== undefined ? clickedActive === item.id : item.isActive,
          })}
        >
          {item.textContent}
        </span>
        {item.children.length > 0 && <ul>{renderList(item.children)}</ul>}
      </li>
    ));
  };

  if (!data.length) {
    return null;
  }

  return (
    <ul className={styles.container} data-testid="table-of-contents">
      {renderList(data)}
    </ul>
  );
});

interface TableOfContentsProps {
  editor: Editor | null;
  content: TableOfContentData;
}
